import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/enhanced-button";
import { MessageSquare, Plus, Trash2, AlertTriangle, ChevronRight, ChevronLeft } from "lucide-react";
import { getConversations, Conversation, deleteConversation } from "@/services/ai.service";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConversationSidebarProps {
  selectedThreadId: string | null;
  onSelectConversation: (threadId: string) => void;
  onNewChat: () => void;
  onDeleteConversation?: (threadId: string) => void;
}

export interface ConversationSidebarRef {
  refreshConversations: () => void;
  updateConversationTitle: (threadId: string) => Promise<void>;
}

const END_OF_CHATS_KEY = "ai-mode-end-of-chats";
const CURRENT_PAGE_KEY = "ai-mode-current-page";

export const ConversationSidebar = forwardRef<ConversationSidebarRef, ConversationSidebarProps>(({
  selectedThreadId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
}, ref) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem('ai-sidebar-expanded');
    return saved === null ? true : saved === 'true';
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = sessionStorage.getItem(CURRENT_PAGE_KEY);
    return savedPage ? parseInt(savedPage, 10) : 1;
  });
  const [hasMoreChats, setHasMoreChats] = useState(() => {
    const endOfChats = sessionStorage.getItem(END_OF_CHATS_KEY);
    return endOfChats !== "true";
  });
  const [hoveredConversationId, setHoveredConversationId] = useState<string | null>(null);
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<Conversation | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async (page: number = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const data = await getConversations(page);

      if (page === 1) {
        setConversations(data.conversations);
      } else {
        setConversations(prev => [...prev, ...data.conversations]);
      }

      setCurrentPage(page);
      sessionStorage.setItem(CURRENT_PAGE_KEY, page.toString());

      const hasMore = data.conversations.length > 0;
      setHasMoreChats(hasMore);

      if (!hasMore) {
        sessionStorage.setItem(END_OF_CHATS_KEY, "true");
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
      if (page > 1) {
        setHasMoreChats(false);
        sessionStorage.setItem(END_OF_CHATS_KEY, "true");
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreChats = async () => {
    if (loadingMore || !hasMoreChats) return;
    await loadConversations(currentPage + 1);
  };

  const handleDeleteClick = (conversation: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    setConversationToDelete(conversation);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!conversationToDelete) return;

    try {
      setDeletingConversationId(conversationToDelete.id);
      setShowDeleteModal(false);

      await deleteConversation(conversationToDelete.id);
      setConversations(prev => prev.filter(c => c.id !== conversationToDelete.id));

      if (conversationToDelete.id === selectedThreadId && onDeleteConversation) {
        onDeleteConversation(conversationToDelete.id);
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      alert("Failed to delete conversation. Please try again.");
    } finally {
      setDeletingConversationId(null);
      setConversationToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setConversationToDelete(null);
  };

  const updateConversationTitle = async (threadId: string) => {
    try {
      const allConversations = await getConversations(1);
      const updatedConversation = allConversations.conversations.find(c => c.id === threadId);

      if (updatedConversation) {
        setConversations(prev => {
          const existingIndex = prev.findIndex(c => c.id === threadId);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = updatedConversation;
            return updated;
          } else {
            return [updatedConversation, ...prev];
          }
        });
      }
    } catch (error) {
      console.error('Failed to update conversation title:', error);
    }
  };

  useImperativeHandle(ref, () => ({
    refreshConversations: () => loadConversations(1),
    updateConversationTitle,
  }));

  const formatDate = (date: Date | string) => {
    const messageDate = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    }
  };

  const totalCount = conversations.length + (selectedThreadId && !conversations.find(c => c.id === selectedThreadId) ? 1 : 0);

  return (
    <div
      className={cn(
        "border-r border-border bg-card flex flex-col h-full transition-all duration-300 overflow-hidden flex-shrink-0",
        isExpanded ? "w-64" : "w-14"
      )}
    >
      {/* Header — unified layout, text slides in with CSS */}
      <div className="border-b border-border flex-shrink-0 flex items-center justify-center p-3 h-14">
        <button
          onClick={onNewChat}
          title="New Chat"
          className={cn(
            "relative flex items-center transition-all duration-300 rounded-lg",
            "bg-primary text-primary-foreground hover:bg-primary/90",
            isExpanded ? "w-full px-3 py-2" : "w-8 h-8 justify-center"
          )}
        >
          <Plus className="w-4 h-4 flex-shrink-0" />
          {!isExpanded && totalCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-primary-foreground text-primary text-[9px] font-bold flex items-center justify-center px-0.5">
              {totalCount > 99 ? "99+" : totalCount}
            </span>
          )}
          <span
            className={cn(
              "font-medium text-sm whitespace-nowrap overflow-hidden",
              "transition-[max-width,opacity,margin-left] duration-300 ease-in-out",
              isExpanded ? "max-w-[160px] opacity-100 ml-2" : "max-w-0 opacity-0 ml-0"
            )}
          >
            New Chat
          </span>
        </button>
      </div>

      {/* Conversation list — always rendered, text slides in with CSS */}
      <ScrollArea className="flex-1">
        <div className={cn("py-2 space-y-0.5", isExpanded ? "px-2" : "px-3 flex flex-col items-center")}>
          {loading ? (
            <div className="flex justify-center pt-3">
              <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30 border-t-primary animate-spin" />
            </div>
          ) : (
            <>
              {/* New unsaved chat */}
              {selectedThreadId && !conversations.find(c => c.id === selectedThreadId) && (
                <button
                  onClick={() => onSelectConversation(selectedThreadId)}
                  title="Current chat"
                  className={cn(
                    "flex items-center transition-all duration-300 rounded-lg border cursor-pointer w-full",
                    isExpanded ? "p-2.5" : "w-8 h-8 justify-center",
                    "bg-accent border-primary/30"
                  )}
                >
                  <MessageSquare className="w-4 h-4 text-primary flex-shrink-0" />
                  <div
                    className={cn(
                      "overflow-hidden transition-[max-width,opacity,margin-left] duration-300 ease-in-out min-w-0 flex-1",
                      isExpanded ? "max-w-[220px] opacity-100 ml-2" : "max-w-0 opacity-0 ml-0"
                    )}
                  >
                    <p className="text-sm font-medium text-foreground truncate leading-tight">New Chat</p>
                    <p className="text-xs text-muted-foreground">Just now</p>
                  </div>
                </button>
              )}

              {/* Conversation items */}
              {conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation.id)}
                  onMouseEnter={() => setHoveredConversationId(conversation.id)}
                  onMouseLeave={() => setHoveredConversationId(null)}
                  className={cn(
                    "flex items-center cursor-pointer transition-all duration-300 rounded-lg border group",
                    isExpanded ? "p-2.5 w-full" : "w-8 h-8 justify-center",
                    selectedThreadId === conversation.id
                      ? "bg-accent border-primary/30"
                      : "border-transparent hover:bg-muted/60"
                  )}
                  title={!isExpanded ? (conversation.title || "Chat") : undefined}
                >
                  <MessageSquare className={cn(
                    "w-4 h-4 flex-shrink-0",
                    selectedThreadId === conversation.id ? "text-primary" : "text-muted-foreground"
                  )} />
                  <div
                    className={cn(
                      "overflow-hidden transition-[max-width,opacity,margin-left] duration-300 ease-in-out min-w-0 flex-1",
                      isExpanded ? "max-w-[220px] opacity-100 ml-2" : "max-w-0 opacity-0 ml-0"
                    )}
                  >
                    <p className="text-sm font-medium text-foreground truncate leading-tight">
                      {conversation.title || "New Chat"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatDate(conversation.lastMessageTime)}
                    </p>
                  </div>
                  {isExpanded && (
                    <button
                      onClick={(e) => handleDeleteClick(conversation, e)}
                      disabled={deletingConversationId === conversation.id}
                      className={cn(
                        "flex-shrink-0 p-1 hover:bg-destructive/10 rounded transition-all disabled:opacity-50 ml-auto",
                        hoveredConversationId === conversation.id || deletingConversationId === conversation.id
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                      title="Delete conversation"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </button>
                  )}
                </div>
              ))}

              {isExpanded && !loading && conversations.length > 0 && (
                <div className="mt-2">
                  <Button
                    onClick={loadMoreChats}
                    variant="ghost"
                    className="w-full text-xs"
                    disabled={!hasMoreChats || loadingMore}
                  >
                    {loadingMore ? "Loading..." : hasMoreChats ? "Load More" : "End of Chats"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Expand / collapse toggle — same arrow pattern as main sidebar */}
      <div className={cn("border-t border-border flex", isExpanded ? "justify-end px-3 py-2" : "justify-center py-2")}>
        <button
          onClick={() => setIsExpanded(v => {
          const next = !v;
          localStorage.setItem('ai-sidebar-expanded', String(next));
          return next;
        })}
          title={isExpanded ? "Collapse" : "Expand"}
          className="p-1.5 rounded-lg text-muted-foreground/50 hover:text-foreground hover:bg-muted transition-colors"
        >
          {isExpanded ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Conversation
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{conversationToDelete?.title || "New Chat"}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCancelDelete}>Cancel</Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});
